import { useCallback, useState } from 'react';

/** Intro screen — no stepper. */
export const ONBOARDING_INTRO_STEP = 0;

/** Last screen index (SetIntentions). */
export const ONBOARDING_LAST_STEP = 14;

/** Steps that show the header stepper (Name → Notifications). */
export const ONBOARDING_STEPPER_STEPS = 13;

export function onboardingProgressPercent(step: number): number {
  if (step <= ONBOARDING_INTRO_STEP) return 0;
  return Math.min(100, Math.round((step / ONBOARDING_STEPPER_STEPS) * 100));
}

export function useOnboardingFlow(onComplete: () => void) {
  const [step, setStep] = useState(ONBOARDING_INTRO_STEP);

  const goToStep = useCallback((target: number) => {
    setStep(Math.min(Math.max(target, ONBOARDING_INTRO_STEP), ONBOARDING_LAST_STEP));
  }, []);

  const next = useCallback(() => {
    setStep(s => Math.min(s + 1, ONBOARDING_LAST_STEP));
  }, []);

  const back = useCallback(() => {
    setStep(s => {
      if (s <= ONBOARDING_INTRO_STEP) {
        return ONBOARDING_INTRO_STEP;
      }
      return s - 1;
    });
  }, []);

  const skip = useCallback(() => {
    setStep(s => Math.min(s + 1, ONBOARDING_LAST_STEP));
  }, []);

  const finish = useCallback(() => {
    onComplete();
  }, [onComplete]);

  return {
    step,
    setStep: goToStep,
    next,
    back,
    skip,
    finish,
    progress: onboardingProgressPercent(step),
    totalSteps: ONBOARDING_STEPPER_STEPS,
    isIntroStep: step === ONBOARDING_INTRO_STEP,
    isLastStep: step === ONBOARDING_LAST_STEP,
  };
}

export type OnboardingFlow = ReturnType<typeof useOnboardingFlow>;
